import { Modal } from '../../../ui/Modal';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/component/checkbox';
import { MagnifyingGlassIcon, UserIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { Input } from '../../../ui/form/Input';
import type { IPerson } from '../../../types/people';

type AssignEmployeeProps = {
  setIsShownAssignEmployyes: (isShown: boolean) => void;
  peopleOptions: IPerson[];
  handleSelectPeople: (id: string, checked: boolean) => void;
  selectedEmployees: string[];
};

export function AssignEmployee({
  setIsShownAssignEmployyes,
  peopleOptions,
  handleSelectPeople,
  selectedEmployees,
}: AssignEmployeeProps) {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Ensure peopleOptions is always an array
  const peopleList = Array.isArray(peopleOptions) ? peopleOptions : [];
  
  // Filter people based on search term
  const filteredPeople = searchTerm 
    ? peopleList.filter(person => 
        person?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        person?.email?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : peopleList;

  const selectedCount = selectedEmployees.length;

  return (
    <>
      <Modal.Header onClose={() => setIsShownAssignEmployyes(false)}>
        <Modal.Title>Assign people</Modal.Title>
        <Modal.Description>
          Select team members for this rota and confirm your final list.
        </Modal.Description>
      </Modal.Header>
      
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
            </div>
            <Input
              type="text"
              className="pl-10"
              placeholder="Search member by name or email address"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <span className="font-medium text-slate-900">{selectedCount}</span>{' '}
            {selectedCount === 1 ? 'employee selected' : 'employees selected'}
          </div>
          
          <div className="mt-1 max-h-72 overflow-y-auto rounded-xl border border-slate-200">
            {filteredPeople.length > 0 ? (
              filteredPeople.map((person) => (
                <div
                  key={person.id}
                  className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 last:border-b-0 hover:bg-slate-50"
                >
                  <div className="flex items-center">
                    <Checkbox
                      className="rounded-md border-[1px]"
                      color="rose"
                      checked={selectedEmployees.includes(person.id)}
                      onChange={(checked) =>
                        handleSelectPeople(person.id, checked)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    {person?.safe_profile_picture_url ? (
                      <img
                        className="h-8 w-8 rounded-full"
                        src={person.safe_profile_picture_url}
                        alt={person.full_name || 'User'}
                      />
                    ) : (
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200">
                        <span className="text-xs font-medium text-slate-600">
                          {person.full_name ? person.full_name.charAt(0) : '?'}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {person.full_name || 'Unknown User'}
                      </div>
                      <div className="truncate text-xs text-slate-500">
                        {person.email || 'No email address'}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <UserIcon className="h-10 w-10 text-slate-300" />
                <p className="mt-2 text-sm text-slate-500">
                  {searchTerm ? 'No matching employees found' : 'No employees available'}
                </p>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <Button
          variant="outline"
          onClick={() => setIsShownAssignEmployyes(false)}
        >
          Back
        </Button>
        <Button
          color="red"
          onClick={() => setIsShownAssignEmployyes(false)}
        >
          Confirm selection
        </Button>
      </Modal.Footer>
    </>
  );
}
